import { enabled } from "@giftforward/entitlements";
import { useMemo } from "react";
import { useGroups } from "@/hooks/useGroups";
import { useOrgEntitlements } from "@/hooks/useOrgEntitlements";
import { usePermissions } from "@/hooks/usePermissions";
import type { WalletTourDestination } from "@/interfaces/tour";
import { walletTourDestination } from "@/utils/tour-helper";

/**
 * Where the onboarding tour's wallet step has to land for the SIGNED-IN caller's organization.
 *
 * Reads the same two sources OrgDetail reads to decide the arrangement — `useOrgEntitlements` and
 * `useGroups`, keyed on the same organization — so this shares OrgDetail's cache entries rather
 * than fetching the same data a second time.
 *
 * Both queries are gated on the capability half of `isTourEligible`: a non-GFG caller who may edit
 * their COMPANY, i.e. a Client Admin. The provider that calls this is mounted on every
 * authenticated page, so a caller who can never be offered the tour must not pay a request for
 * it — and `GET /admin/groups` requires `view` GROUP, which the other client roles do not hold.
 *
 * Deliberately NOT gated on "the tour is open": the answer has to be known at the moment step 5
 * navigates, and starting the fetch there would race the user's own clicks.
 */
export function useWalletTourDestination(): WalletTourDestination {
  const { can, isGfg, organizationId, defaultGroupId, groupId } = usePermissions();

  const isTourCandidate = !isGfg && organizationId !== null && can("edit", "COMPANY");
  const orgId = isTourCandidate ? organizationId : null;

  const { ent } = useOrgEntitlements(orgId);
  // `useGroups` skips the request for a non-positive id ("no organization picked yet"), which is
  // also what keeps it idle for everyone this tour is not for.
  const { data: groups } = useGroups(orgId ?? 0);

  const canViewCredit = can("view", "CREDIT");
  const { kind, groupId: walletGroupId } = walletTourDestination({
    isGfg,
    hasMultiGroupEntitlement: !!ent && enabled(ent, "wallet", "multi_group"),
    groupCount: groups?.length ?? 0,
    rootGroupId: groups?.find((g) => g.parentId === null)?.id ?? null,
    preferredGroupId: defaultGroupId ?? groupId,
    canViewCredit,
  });

  // Memoised on the DECISION, not on the queries behind it: react-query hands back a new array on
  // every refetch, and a fresh identity here would churn the whole tour context value.
  return useMemo(() => ({ kind, groupId: walletGroupId }), [kind, walletGroupId]);
}
